<script setup lang="ts">
import { BaseHeader, type BaseHeaderImage } from '@components/base';
import { computed } from 'vue';

type ApproachCard = {
  title: string;
  description: string;
  imageUrl: string;
  cardBg?: string | null;
};

const props = withDefaults(
  defineProps<{
    headingHtml?: string;
    headingObjects?: BaseHeaderImage[] | string;
    cards?: ApproachCard[] | string;
    ctaHref?: string;
    ctaTarget?: string;
    ctaText?: string;
    ctaBg?: string | null;
  }>(),
  {
    headingHtml: '',
    headingObjects: () => [],
    cards: () => [],
    ctaHref: '',
    ctaTarget: '_self',
    ctaText: '',
    ctaBg: '',
  },
);

function parseHeadingObjects(raw: BaseHeaderImage[] | string | undefined): BaseHeaderImage[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    let s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
    } catch {
      if (s.includes('&quot;')) {
        s = s.replace(/&quot;/g, '"');
        try {
          const parsed = JSON.parse(s) as unknown;
          return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
        } catch {
          return [];
        }
      }
      return [];
    }
  }
  return [];
}

function parseCards(raw: ApproachCard[] | string | undefined): ApproachCard[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as ApproachCard[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const headingObjectsList = computed(() => parseHeadingObjects(props.headingObjects));
const cardsList = computed(() => parseCards(props.cards));

const headingContentClass = 'font-inter-tight block w-[68%] text-left text-28 font-semibold leading-[1.1] tracking-[-0.01em] sm:text-36 md:text-42 lg:w-full lg:text-center lg:text-52 [&_p]:m-0 [&_p]:text-left [&_p]:leading-[inherit] lg:[&_p]:text-center';

const ctaStyle = computed(() => {
  const bg = props.ctaBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const show = computed(() => {
  const t = (props.headingHtml ?? '').replace(/<[^>]*>/g, '').trim();
  return t.length > 0 || cardsList.value.length > 0 || (props.ctaHref ?? '').trim().length > 0;
});

function cardBgStyle(card: ApproachCard) {
  const bg = card.cardBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
}

function linearChannel(u: number): number {
  return u <= 0.03928 ? u / 12.92 : ((u + 0.055) / 1.055) ** 2.4;
}

function isLightBg(card: ApproachCard): boolean {
  const hex = card.cardBg?.trim();
  if (!hex || !hex.startsWith('#')) return true;
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized.split('').map((c) => c + c).join('');
  }
  if (normalized.length !== 6) return true;
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  const luma = 0.2126 * linearChannel(r) + 0.7152 * linearChannel(g) + 0.0722 * linearChannel(b);
  return luma > 0.508;
}
</script>

<template>
  <section
    v-if="show"
    class="pricing-approach bg-off-white-500 lg:py-72 py-40 md:py-56"
  >
    <div class="container">
      <div class="px-20 md:px-0">
        <div class="text-off-primary-500 mx-auto max-w-[900px] text-left lg:text-center">
          <BaseHeader
            v-if="headingHtml"
            as="h2"
            :unwrap-outer-paragraph="true"
            :content="headingHtml"
            :objects="headingObjectsList"
            :content-class="`${headingContentClass}`"
          />
        </div>
        <div
          v-if="cardsList.length"
          class="mt-28 flex flex-col gap-16 lg:mt-36 lg:grid lg:grid-cols-4 lg:gap-14"
        >
          <article
            v-for="(card, index) in cardsList"
            :key="`${index}-${card.title}`"
            class="flex min-h-[200px] flex-row overflow-hidden rounded-[24px] shadow-sm lg:h-[440px] lg:flex-col"
            :style="cardBgStyle(card)"
          >
            <div
              class="flex flex-1 flex-col justify-center px-20 py-24 pr-16 lg:flex-none lg:justify-start lg:px-24 lg:pb-16 lg:pt-28"
              :class="isLightBg(card) ? 'text-off-primary-500' : 'text-white'"
            >
              <h3
                class="font-inter-tight text-20 font-semibold leading-125 lg:text-22"
                v-html="card.title"
              />
              <p
                v-if="card.description"
                class="mt-10 text-14 leading-[1.35] lg:mt-12"
                :class="isLightBg(card) ? 'text-off-primary-500' : 'text-white/95'"
                v-html="card.description"
              />
            </div>
            <div
              v-if="card.imageUrl"
              class="relative -ml-32 w-[42%] max-w-[160px] shrink-0 self-stretch lg:-ml-0 lg:min-h-[220px] lg:w-full lg:max-w-none lg:flex-1"
            >
              <img
                :src="card.imageUrl"
                alt=""
                class="absolute inset-0 size-full object-cover object-center lg:scale-110"
                loading="lazy"
                decoding="async"
              >
            </div>
          </article>
        </div>

        <div
          v-if="ctaHref"
          class="mt-28 xl:mt-36 xl:flex xl:justify-center"
        >
          <a
            :href="ctaHref"
            :target="ctaTarget || '_self'"
            class="flex w-full items-center justify-center rounded-full px-28 py-16 text-16 font-semibold text-white transition-opacity hover:opacity-90 xl:inline-flex xl:w-auto xl:px-32 xl:py-14 xl:text-14"
            :class="{ 'bg-deep-blue': !ctaBg || !ctaBg.trim() }"
            :style="ctaStyle"
          >
            {{ ctaText }}
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Inline img1 chip: match line box so wrapped lines don’t pick up huge line-gap (BaseHeader defaults are wider/taller). */
.pricing-approach :deep(.base-header__chip) {
  width: 2.35em !important;
  height: 1.2em !important;
  min-height: 1.2em !important;
  max-height: 1.2em !important;
  vertical-align: -0.06em;
}

.pricing-approach :deep(.base-header__chip-img) {
  height: 100% !important;
  width: 100% !important;
  object-fit: cover;
}
</style>
